import type { LinkProps } from 'next/link';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useState } from 'react';

type ActiveLinkProps = LinkProps & {
  className?: string;
  activeClassName: string;
  checkBlogActive?: boolean;
};

const isBlogActive = (pathname: string): boolean => {
  return pathname.includes('/blog');
};

const isPathActive = (linkPathname: string, activePathname: string): boolean => {
  return activePathname === linkPathname || activePathname.startsWith(`${linkPathname}/`);
};

const ActiveLink = ({ children, activeClassName, className, checkBlogActive = false, ...props }: PropsWithChildren<ActiveLinkProps>) => {
  const router = useRouter();
  const { asPath, isReady } = router;
  const [computedClassName, setComputedClassName] = useState(className);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isReady) return;

    const activePathname = new URL(asPath, window.location.href).pathname;
    const nextIsActive = checkBlogActive ? isBlogActive(activePathname) : isPathActive(new URL((props.as || props.href) as string, window.location.href).pathname, activePathname);

    const newClassName = nextIsActive ? `${className} ${activeClassName}`.trim() : className;

    if (newClassName !== computedClassName) {
      setComputedClassName(newClassName);
    }

    if (nextIsActive !== isActive) {
      setIsActive(nextIsActive);
    }
  }, [asPath, isReady, props.as, props.href, activeClassName, className, computedClassName, checkBlogActive, isActive]);

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!isReady) {
        return;
      }

      e.preventDefault();

      const href = props.href.toString();

      if (asPath === href) {
        return;
      }

      await router.push(href);

      if (props.onClick) {
        props.onClick(e);
      }
    },
    [props, router, asPath, isReady]
  );

  return (
    <Link className={computedClassName ?? ''} aria-current={isActive ? 'page' : undefined} {...props} onClick={handleClick}>
      {children}
    </Link>
  );
};

export default ActiveLink;
